import { useEffect, useRef } from 'react';
import internalTaskService from '../services/internalTaskService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

type NotificationEvent =
  | { type: 'tasks_count_changed' }
  | {
      type: 'video_patient_joined';
      session_id: number;
      external_id: string;
      source: string;
      patient_name: string | null;
      room_name: string;
    }
  | { type: string };

export interface VideoPatientJoined {
  session_id: number;
  external_id: string;
  source: string;
  patient_name: string | null;
  room_name: string;
}

/**
 * Opens an SSE connection to the backend (`GET /events?token=`) while authenticated and
 * refreshes the internal-tasks "needs action" count whenever the server signals a change.
 *
 * The token is passed in the query string because the EventSource API cannot set headers.
 * On connection error the stream is reconnected after a short delay. Cleans up on
 * logout/unmount.
 */
export function useTaskNotifications(
  isAuthenticated: boolean,
  onTasksCount: (count: number) => void,
  onVideoPatientJoined?: (event: VideoPatientJoined) => void,
) {
  const esRef = useRef<EventSource | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Keep the latest callback without forcing the effect to re-run (which would
  // tear down and re-open the SSE connection on every render).
  const onTasksCountRef = useRef(onTasksCount);
  useEffect(() => {
    onTasksCountRef.current = onTasksCount;
  }, [onTasksCount]);
  const onVideoPatientJoinedRef = useRef(onVideoPatientJoined);
  useEffect(() => {
    onVideoPatientJoinedRef.current = onVideoPatientJoined;
  }, [onVideoPatientJoined]);

  useEffect(() => {
    if (!isAuthenticated) {
      cleanup();
      return;
    }

    connect();
    return cleanup;

    function connect() {
      const token = localStorage.getItem('token');
      if (!token) return;

      const es = new EventSource(`${API_BASE_URL}/events?token=${token}`);
      esRef.current = es;

      es.onmessage = (e) => {
        try {
          const event: NotificationEvent = JSON.parse(e.data);
          if (event.type === 'tasks_count_changed') {
            internalTaskService
              .getNeedsActionCount()
              .then((count) => onTasksCountRef.current(count))
              .catch(() => {});
          } else if (event.type === 'video_patient_joined') {
            onVideoPatientJoinedRef.current?.(event as VideoPatientJoined);
          }
        } catch {
          // Ignore keepalives / unparseable payloads.
        }
      };

      es.onerror = () => {
        es.close();
        esRef.current = null;
        reconnectTimerRef.current = setTimeout(connect, 5000);
      };
    }

    function cleanup() {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (esRef.current) {
        esRef.current.close();
        esRef.current = null;
      }
    }
  }, [isAuthenticated]);
}

export default useTaskNotifications;
